import { HexColorPicker } from 'react-colorful';
import { useState } from 'react';
import { MousePointer2, Type, Stamp, Pen, Undo2, Download, Send, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export type AnnotationMode = 'pointer' | 'text' | 'stamp' | 'freehand';
export type StampType = 'approved' | 'approved_as_noted' | 'revise_resubmit' | 'rejected';
/** Review context that controls which annotation tools are visible (D-Q22). */
export type ReviewMode = 'comment' | 'approve' | 'sign';

export const STAMP_LABELS: Record<StampType, string> = {
  approved: 'Approved',
  approved_as_noted: 'Approved as Noted',
  revise_resubmit: 'Revise & Resubmit',
  rejected: 'Rejected',
};

interface AnnotationToolbarProps {
  mode: AnnotationMode;
  stampType: StampType;
  freehandColor: string;
  onModeChange: (mode: AnnotationMode) => void;
  onStampTypeChange: (type: StampType) => void;
  onColorChange: (color: string) => void;
  onUndo: () => void;
  onExportPdf: () => void;
  onSendForReview: () => void;
  /** F1: optional Confirm-Approval handler — only used when the stamp tool is
   *  gated off for the tenant (in_canvas_stamps_enabled=false) and the review
   *  is in approve mode. Renders a primary "Confirm approval" button in the
   *  toolbar instead of the stamp dropdown. */
  onConfirmApproval?: () => void;
  canUndo: boolean;
  exporting?: boolean;
  /** Review mode gates which annotation tools are available (D-Q22).
   *  comment: pointer + text + freehand only.
   *  approve: all four tools including stamp.
   *  sign: handled by R4 (same visible set as comment for now). */
  reviewMode?: ReviewMode;
  /** Hide admin-only actions (Export PDF, Send for Review) on the portal. */
  isPortal?: boolean;
  /** Portal-only download handler — when provided, surfaces a Download button
   *  so the customer can pull the sealed PDF bytes after R6 lock. */
  onDownloadPortalFile?: () => void;
  /** True while the portal download fetch is in flight (button disabled state). */
  portalDownloading?: boolean;
  /** F1: tenant-level gate for the in-canvas stamp tool. When false AND mode
   *  is 'approve', the stamp tool button + dropdown are hidden so Canadian
   *  workflows can rely on the external stamper instead. Default true keeps
   *  backwards compatibility for tenants without the setting. */
  inCanvasStampsEnabled?: boolean;
  /** Iter 3 B3: when the review is locked AND the viewer is in portal context,
   *  hide every annotation tool + the Confirm approval button. The customer
   *  is past the action — toolbar reduces to view-only chrome (Undo disabled,
   *  Download, Close). Without this, F30/F31 still showed text/pen/stamp + a
   *  stray Confirm approval button on a sealed review. */
  viewOnly?: boolean;
}

const ALL_TOOL_BUTTONS: { mode: AnnotationMode; icon: React.ElementType; label: string }[] = [
  { mode: 'pointer', icon: MousePointer2, label: 'Pointer' },
  { mode: 'text', icon: Type, label: 'Text Comment' },
  { mode: 'stamp', icon: Stamp, label: 'Stamp' },
  { mode: 'freehand', icon: Pen, label: 'Freehand' },
];

function visibleTools(
  reviewMode: ReviewMode,
  inCanvasStampsEnabled: boolean,
): AnnotationMode[] {
  if (reviewMode === 'approve') {
    // F1: Canadian-style workflow gate. When the tenant has disabled in-canvas
    // stamps (e.g. Banks Architecture relying on external stamper), drop the
    // stamp tool from approve mode entirely.
    if (!inCanvasStampsEnabled) return ['pointer', 'text', 'freehand'];
    return ['pointer', 'text', 'stamp', 'freehand'];
  }
  // comment and sign: no stamp tool (sign tool added in R4)
  return ['pointer', 'text', 'freehand'];
}

// AnnotationToolbar — document-review variant (R2: mode-gated tools per D-Q22)
export function AnnotationToolbar({
  mode,
  stampType,
  freehandColor,
  onModeChange,
  onStampTypeChange,
  onColorChange,
  onUndo,
  onExportPdf,
  onSendForReview,
  onConfirmApproval,
  canUndo,
  exporting = false,
  reviewMode = 'approve',
  isPortal = false,
  onDownloadPortalFile,
  portalDownloading = false,
  inCanvasStampsEnabled = true,
  viewOnly = false,
}: AnnotationToolbarProps) {
  const allowed = viewOnly ? [] : visibleTools(reviewMode, inCanvasStampsEnabled);
  const [colorOpen, setColorOpen] = useState(false);

  return (
    <div className="flex items-center gap-2 p-2 bg-background border-b flex-wrap">
      {/* Tool mode buttons — filtered by reviewMode */}
      <div className="flex items-center gap-1 border rounded-md p-0.5">
        {ALL_TOOL_BUTTONS.filter(({ mode: m }) => allowed.includes(m)).map(({ mode: m, icon: Icon, label }) => (
          <Button
            key={m}
            variant={mode === m ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 px-2"
            title={label}
            onClick={() => onModeChange(m)}
          >
            <Icon className="w-3.5 h-3.5" />
            <span className="sr-only">{label}</span>
          </Button>
        ))}
      </div>

      {/* Stamp type selector — only shown in stamp mode AND when stamp is an allowed tool */}
      {mode === 'stamp' && allowed.includes('stamp') && (
        <Select value={stampType} onValueChange={(v) => onStampTypeChange(v as StampType)}>
          <SelectTrigger className="h-7 text-xs w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.entries(STAMP_LABELS) as [StampType, string][]).map(([value, label]) => (
              <SelectItem key={value} value={value} className="text-xs">
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Freehand color picker — only shown in freehand mode */}
      {mode === 'freehand' && (
        <Popover open={colorOpen} onOpenChange={setColorOpen}>
          <PopoverTrigger asChild>
            <button
              className="h-7 w-7 rounded border border-input flex-shrink-0"
              style={{ backgroundColor: freehandColor }}
              title="Freehand color"
            />
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="start">
            <HexColorPicker color={freehandColor} onChange={onColorChange} />
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs text-muted-foreground font-mono">{freehandColor}</span>
            </div>
          </PopoverContent>
        </Popover>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Undo — hidden entirely when the review is view-only (locked or portal
       *  + locked). On a locked review Undo is a no-op because no annotation
       *  mutations can land; rendering it as a disabled stub adds noise. */}
      {!viewOnly && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2"
          onClick={onUndo}
          disabled={!canUndo}
          title="Undo last annotation"
        >
          <Undo2 className="w-3.5 h-3.5" />
          <span className="ml-1 text-xs">Undo</span>
        </Button>
      )}

      {/* Admin-only mutation actions. Hidden in portal AND on locked reviews:
       *  - Export PDF still works as a read action, but pairs with mutation
       *    UI (Send for Review). Keep the pairing consistent: locked review
       *    drops both so the toolbar reads "this is past the gate."
       *  - Send for Review is a no-op on locked (the review is sealed). */}
      {!isPortal && !viewOnly && (
        <>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={onExportPdf}
            disabled={exporting}
            title="Export flat PDF with annotations baked in"
          >
            <Download className="w-3 h-3 mr-1" />
            {exporting ? 'Exporting…' : 'Export PDF'}
          </Button>

          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={onSendForReview}
            title="Send annotated PDF to engineers for review"
          >
            <Send className="w-3 h-3 mr-1" />
            Send for Review
          </Button>
        </>
      )}

      {/* F1: Confirm-approval primary action — surfaces only when the
       *  tenant has disabled in-canvas stamps AND the review is in approve
       *  mode. Provides a stamp-free path to mark the review approved.
       *  Visible to both admin AND portal so the customer can complete
       *  approval when their tenant has disabled in-canvas stamps.
       *  Iter3 B3: viewOnly (portal + locked) suppresses the button — the
       *  review is past the approval gate, no second confirm makes sense. */}
      {!viewOnly && reviewMode === 'approve' && !inCanvasStampsEnabled && onConfirmApproval && (
        <Button
          variant="default"
          size="sm"
          className="h-7 text-xs"
          onClick={onConfirmApproval}
          title="Approve this review without dropping an in-canvas stamp"
          data-testid="confirm-approval-button"
        >
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Confirm approval
        </Button>
      )}

      {/* Portal-only Download button — pulls the sealed PDF bytes (or original
       *  when not yet stamped). Token-gated, no Firebase auth required. */}
      {isPortal && onDownloadPortalFile && (
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={onDownloadPortalFile}
          disabled={portalDownloading}
          title="Download the PDF — sealed bytes when the review is locked"
          data-testid="portal-download-pdf"
        >
          <Download className="w-3 h-3 mr-1" />
          {portalDownloading ? 'Downloading…' : 'Download'}
        </Button>
      )}
    </div>
  );
}
